import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id, isRoute) => {
    if (isRoute) {
      navigate(`/${id}`);
      setIsOpen(false);
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const navigation = [
    { name: 'Accueil', id: 'accueil' },
    { name: 'Services', id: 'services' },
    { name: 'À Propos', id: 'about' },
    { name: 'Témoignages', id: 'temoignages' },
    { name: 'Blog', id: 'blog', isRoute: true },
    { name: 'Contact', id: 'contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md border-b border-amber-500/10' : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button onClick={() => scrollToSection('accueil')} className="flex items-center space-x-2" data-testid="header-logo">
            <span className="text-xl font-bold text-amber-400" style={{fontFamily: 'Cormorant Garamond, serif'}}>Marabout Genève</span>
            <span className="hidden sm:inline text-amber-200/50 text-xs">| Voyant Médium Africain</span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id, item.isRoute)}
                className="text-sm text-amber-200/70 hover:text-amber-400 transition-colors"
                data-testid={`nav-${item.id}`}
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            <a
              href="tel:+41767836186"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-4 py-2 rounded-lg text-sm hover:from-amber-400 hover:to-amber-500 transition-all"
              data-testid="header-call-btn"
            >
              <Phone className="w-4 h-4" />
              <span>Appeler</span>
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden text-amber-400 p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden bg-black/95 border-t border-amber-500/10 py-4">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id, item.isRoute)}
                className="block w-full text-left px-4 py-3 text-amber-200/70 hover:text-amber-400 hover:bg-amber-500/5 transition-colors"
              >
                {item.name}
              </button>
            ))}
            <div className="px-4 pt-4 space-y-2">
              <a href="tel:+41767836186" className="block w-full text-center bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-4 py-3 rounded-lg">
                <Phone className="w-4 h-4 inline mr-2" />+41 76 783 61 86
              </a>
              <a href="https://wa.me/41767836186" target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-green-600 text-white font-bold px-4 py-3 rounded-lg">
                WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
